import fetch from 'node-fetch';
import {
  ApidaeOuverturePeriod,
  extractEventPeriodFromApidaeObject,
  isApidaeEventType,
} from './apidaeEventDates';

/**
 * Équivalent Python :
 * re.sub(r'[-\s]+', '-', re.sub(r'[^\w\s-]', '', unicodedata.normalize('NFKD', text).encode('ASCII', 'ignore').decode('ASCII').strip().lower()))
 */
export function slugify(text: string): string {
  if (text == null || typeof text !== 'string') return '';

  const nfkd = text.normalize('NFKD');
  let ascii = '';
  for (let i = 0; i < nfkd.length; i++) {
    const c = nfkd.charCodeAt(i);
    if (c < 128) ascii += nfkd[i];
  }

  const stripped = ascii.trim().toLowerCase();
  const withoutBad = stripped.replace(/[^\w\s-]/g, '');
  return withoutBad.replace(/[-\s]+/g, '-');
}

/** Même logique que `generate_seller_slug(name, provider_id)` côté API (slug Apidae / Cibler). */
export function generateSellerSlug(name: string, providerId: string): string {
  return `${slugify(name)}-${providerId}`;
}

export interface ApidaeGridItem {
  id: number;
  storeName: string;
  city: string;
  urlimg: string | null;
  categslug: string;
  store_name: string;
  seller_id: number;
  gifts: null;
  note: number;
  norm: string;
  _geoloc: { lat: number; lng: number } | null;
  /** Lien fiche : `/fiche-sit/{slugify(nom)}-{id_apidae}` (aligné Cibler) */
  detailUrl: string;
  label?: string;
  apidaeType?: string;
  /** Période d’ouverture retenue (événements / festivals uniquement) */
  eventPeriod?: ApidaeOuverturePeriod | null;
}

/**
 * Slugs alignés sur les catégories site + assets CDN (`pin/{slug}.png`, `categSearch/{slug}.*`).
 * Les anciennes valeurs (ex. decouvrir, hotellerie) n’existent pas sur le CDN → ImageEngine renvoie
 * une grande image de substitution utilisée comme icône de marqueur.
 */
const defaultCategSlug = 'voyages';

const apidaeTypeToCategslug: Record<string, string> = {
  HOTELLERIE: 'ou-sejourner',
  HEBERGEMENT_LOCATIF: 'ou-sejourner',
  HOTELLERIE_PLEIN_AIR: 'ou-sejourner',
  CAMPING: 'ou-sejourner',
  RESTAURATION: 'a-table',
  FAST_FOOD: 'a-table',
  PATRIMOINE_CULTUREL: 'monuments-et-musees',
  EQUIPEMENT_LOISIRS: 'loisirs',
  COMMERCE_ET_SERVICE: 'commerce',
  MAGASIN: 'commerce',
  PRODUCTEUR: 'producteurs-et-artisans',
  TASTING_PROVIDER: 'producteurs-et-artisans',
  TERRITOIRE: 'voyages',
  ORGANISME_TOURISME: 'voyages',
  FETE_ET_MANIFESTATION: 'agenda',
  FESTIVAL: 'agenda',
  EVENEMENT: 'agenda',
  ACTIVITE: 'loisirs',
  CIRCUIT: 'loisirs',
  ITINERAIRE: 'loisirs',
  AGENCE_DE_VOYAGES: 'voyages',
  TRANSPORT: 'voyages',
};

function normalizeNorm(s: string) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function mapApidaeListItemToGridItem(o: Record<string, unknown>): ApidaeGridItem | null {
  if (!o || typeof o !== 'object' || o.id == null) return null;

  const nom = (o.nom as { libelleFr?: string; libelleEn?: string } | undefined)?.libelleFr
    ?? (o.nom as { libelleFr?: string; libelleEn?: string } | undefined)?.libelleEn
    ?? String(o.id);

  const coords = (o.localisation as { geolocalisation?: { geoJson?: { coordinates?: number[] } } } | undefined)
    ?.geolocalisation?.geoJson?.coordinates;
  const lng = typeof coords?.[0] === 'number' ? coords[0] : null;
  const lat = typeof coords?.[1] === 'number' ? coords[1] : null;

  const city =
    (o.localisation as { adresse?: { commune?: { nom?: string } } } | undefined)?.adresse?.commune?.nom ?? '';

  const img =
    (o.illustrations as { traductionFichiers?: { url?: string }[] }[] | undefined)?.[0]?.traductionFichiers?.[0]
      ?.url ?? null;

  const providerId = String(o.id);
  const pathSlug = generateSellerSlug(nom, providerId);
  const detailUrl = `/fiche-sit/${encodeURIComponent(pathSlug)}`;

  const typeStr = typeof o.type === 'string' ? o.type.toUpperCase().trim() : '';
  const categslug = apidaeTypeToCategslug[typeStr] ?? defaultCategSlug;

  const eventPeriod = isApidaeEventType(typeStr) ? extractEventPeriodFromApidaeObject(o) : null;

  return {
    id: Number(o.id),
    storeName: nom,
    city,
    urlimg: img,
    categslug,
    store_name: nom,
    seller_id: Number(o.id),
    gifts: null,
    note: 0,
    norm: normalizeNorm(`${nom}_${city}_${typeStr}`),
    _geoloc: lat != null && lng != null ? { lat, lng } : null,
    detailUrl,
    apidaeType: typeStr || undefined,
    eventPeriod,
  };
}

export async function fetchApidaeSelectionObjects(
  selectionId: number,
  config: { projectId: number; apiKey: string } | undefined,
  options?: { first?: number; count?: number }
): Promise<ApidaeGridItem[]> {
  const { projectId, apiKey } = config || {};
  if (!selectionId || !projectId || !apiKey) {
    return [];
  }

  const query = {
    apiKey,
    projetId: projectId,
    selectionIds: [selectionId],
    first: options?.first ?? 0,
    count: Math.min(Math.max(options?.count ?? 200, 1), 500),
    responseFields: ['@default', 'ouverture'],
  };

  const url = `https://api.apidae-tourisme.com/api/v002/recherche/list-objets-touristiques?query=${encodeURIComponent(
    JSON.stringify(query)
  )}`;

  try {
    const res = await fetch(url);
    const json = (await res.json()) as {
      errorType?: string;
      message?: string;
      objetsTouristiques?: Record<string, unknown>[];
    };

    if (json.errorType || (json.message && !json.objetsTouristiques)) {
      console.error('[fetchApidaeSelectionObjects]', json.message || json.errorType);
      return [];
    }

    const list = json.objetsTouristiques || [];
    return list.map(mapApidaeListItemToGridItem).filter(Boolean) as ApidaeGridItem[];
  } catch (e) {
    console.error('[fetchApidaeSelectionObjects]', e);
    return [];
  }
}
